import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal, SectionLabel } from "@/components/common/Reveal";
import { HealthDisclaimer } from "@/components/common/HealthDisclaimer";
import { sources } from "@/data/site";
import { visibleApps } from "@/data/apps";

export default function Sources() {
  const healthApps = visibleApps.filter((a) => a.isHealthApp);

  return (
    <>
      <Seo
        title="Sources & references"
        path="/sources"
        description="Reference organisations and publications behind the educational content in tutinsapp health apps."
      />
      <PageHeader
        label="Transparency"
        lines={["Sources &", "references."]}
        intro="Educational content in our health apps is written from published guidance by recognised health organisations. Entries are added here only once confirmed — placeholders below are clearly marked and will be replaced with the exact publications and links."
      />

      <section className="pb-16">
        <div className="shell">
          <HealthDisclaimer testId="sources-health-disclaimer" />
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="shell">
          <SectionLabel>Reference list</SectionLabel>
          <ul className="mt-10 space-y-0" data-testid="sources-list">
            {sources.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <li
                  data-testid={`source-item-${s.id}`}
                  className="grid gap-3 py-8 sm:grid-cols-[1.1fr_1.4fr] sm:gap-10"
                  style={{ borderTop: "1px solid var(--line)" }}
                >
                  <div>
                    <h3 className="font-display text-lg font-bold leading-snug text-white sm:text-xl">
                      {s.organization}
                    </h3>
                    <p className="mt-2 flex flex-wrap gap-2 font-mono-x text-[11px] uppercase tracking-[0.14em] text-gray-500">
                      {s.apps?.map((appId) => (
                        <span key={appId}>
                          supports:{" "}
                          <Link
                            to={`/apps/${appId}`}
                            className="link-underline"
                            style={{ color: "var(--accent)" }}
                          >
                            {appId}
                          </Link>
                        </span>
                      ))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-300">{s.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-500">{s.description}</p>
                    {s.url ? (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`source-link-${s.id}`}
                        className="link-underline mt-4 inline-flex items-center gap-1.5 text-sm"
                        style={{ color: "var(--accent)" }}
                      >
                        Open source <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <p
                        data-testid={`source-pending-${s.id}`}
                        className="mt-4 font-mono-x text-[11px] uppercase tracking-[0.14em] text-gray-600"
                      >
                        Link pending
                      </p>
                    )}
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>

          <div
            className="mt-14 rounded-3xl p-6 sm:p-8"
            style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
            id="disclaimer"
          >
            <p className="label mb-3">How to update this page</p>
            <p className="text-sm leading-relaxed text-gray-400">
              Add or edit entries in{" "}
              <span className="font-mono-x text-xs text-gray-300">src/data/site.js</span> under{" "}
              <span className="font-mono-x text-xs text-gray-300">sources</span>. Each entry takes an
              organisation, title, short description, link and the app IDs it supports — it then
              appears here and on each linked app page.
            </p>
            {healthApps.length > 0 && (
              <p className="mt-4 text-sm text-gray-500">
                Currently linked health apps:{" "}
                {healthApps.map((a) => a.name).join(", ")}.
              </p>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
